// src/pages/Home.js
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import AnimatedBackground from '../components/AnimatedBackground';
import CustomerReviews from '../components/CustomerReviews';
import {
  FaMobileAlt,
  FaTabletAlt,
  FaLaptop,
  FaGamepad,
  FaClock,
  FaHeadphones,
} from 'react-icons/fa';

function Home() {
  const { settings } = useSettings();
  // Create a ref for the device selection section
  const deviceSectionRef = useRef(null);

  // Scroll handler to bring the device selection section into view
  const handleBookClick = () => {
    if (deviceSectionRef.current) {
      // Optionally adjust the offset if you have a fixed header, etc.
      const yOffset = -150; // Adjust this value as needed
      const y =
        deviceSectionRef.current.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* ========== DYNAMIC HERO SECTION ========== */}
      <section
        className="hero-section"
        style={{
          position: "relative",
          width: "100vw",
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          minHeight: "600px",
          overflow: "hidden",
        }}
      >
        {/* Dynamic Background */}
        {settings.homeHero.backgroundType === 'video' && settings.homeHero.backgroundMedia && (
          <video
            key={settings.homeHero.backgroundMedia}
            className="hero-video"
            autoPlay
            loop
            muted
            playsInline
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 1,
            }}
          >
            <source src={`${settings.homeHero.backgroundMedia}?t=${Date.now()}`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        
        {settings.homeHero.backgroundType === 'image' && settings.homeHero.backgroundMedia && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${settings.homeHero.backgroundMedia})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: 1,
            }}
          />
        )}
        
        {settings.homeHero.backgroundType === 'animation' && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1,
            }}
          >
            <AnimatedBackground />
          </div>
        )}

        {/* Dynamic Overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: `${settings.homeHero.overlayColor}${Math.round(settings.homeHero.overlayOpacity * 255).toString(16).padStart(2, '0')}`,
            zIndex: 2,
          }}
        />

        {/* Dynamic Hero Content */}
        <div
          className="hero-content"
          style={{
            position: "absolute",
            zIndex: 3,
            bottom: "1.5rem",
            left: "2.5rem",
            maxWidth: "600px",
          }}
        >
          <h1 className="hero-title" style={{
            fontSize: "3.5rem",
            marginBottom: "0.5rem",
            background: "linear-gradient(135deg, #007BFF 0%, #0056b3 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontWeight: "bold",
            lineHeight: "1.2",
            textAlign: "left",
            filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))"
          }}>
            {settings.homeHero.title}
          </h1>
          {settings.homeHero.subtitle && (
            <p className="hero-subtitle" style={{
              fontSize: "1.5rem",
              marginBottom: "1rem",
              color: "#FFFFFF",
              textShadow: "2px 2px 6px rgba(0, 0, 0, 0.9)",
              fontWeight: "500",
              lineHeight: "1.4",
              textAlign: "left"
            }}>
              {settings.homeHero.subtitle}
            </p>
          )}
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.5rem", maxWidth: "400px" }}>
            <button
              onClick={handleBookClick}
              className="hero-btn-primary"
              style={{
                padding: "0.75rem 2rem",
                fontSize: "1rem",
                fontWeight: "600",
                border: "none",
                background: "#FFC107",
                color: "#6c757d",
                cursor: "pointer",
                borderRadius: "25px",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 15px rgba(255, 193, 7, 0.3)"
              }}
            >
              Book Repair Now
            </button>
            <Link
              to="/contact"
              className="hero-btn-outline"
              style={{
                padding: "0.75rem 2rem",
                fontSize: "1rem",
                fontWeight: "600",
                border: "2px solid #FFC107",
                background: "transparent",
                color: "#FFC107",
                textDecoration: "none",
                display: "inline-block",
                borderRadius: "25px",
                transition: "all 0.3s ease"
              }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* ========== FEATURES SECTION ========== */}
      <section
        style={{
          backgroundColor: "#f8f9fa",
          padding: "3rem 1rem",
          textAlign: "center",
          marginTop: "0"
        }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "2rem", marginBottom: "2rem", color: "#6c757d" }}>Why Choose Kentech Phones</h2>
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              gap: "2rem",
              flexWrap: "wrap"
            }}>
            <div style={{ flex: "1", minWidth: "250px" }}>
              <h3 style={{ color: "#007BFF", marginBottom: "0.5rem" }}>Premium Parts</h3>
              <p style={{ color: "#6c757d" }}>We only use premium and original parts for all repairs</p>
            </div>
            <div style={{ flex: "1", minWidth: "250px" }}>
              <h3 style={{ color: "#007BFF", marginBottom: "0.5rem" }}>Warranty Included</h3>
              <p style={{ color: "#6c757d" }}>We provide a warranty on all repairs, varying according to the parts used—original or high-quality certified components—so you can trust your device is in safe hands.</p>
            </div>
            <div style={{ flex: "1", minWidth: "250px" }}>
              <h3 style={{ color: "#007BFF", marginBottom: "0.5rem" }}>Fast Service</h3>
              <p style={{ color: "#6c757d" }}>The repair process begins once we receive your device at our lab. Most repairs are completed within 24 hours; however, completion time may vary depending on the type and complexity of the repair.</p>
            </div>
            <div style={{ flex: "1", minWidth: "250px" }}>
              <h3 style={{ color: "#007BFF", marginBottom: "0.5rem" }}>Mail-In Service</h3>
              <p style={{ color: "#6c757d" }}>We provide our customers with complimentary next-day delivery, ensuring a fast, convenient, and hassle-free repair experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== HOME PAGE CONTENT (Centered) ========== */}
      <div className="home-page" style={{ margin: "0 auto", maxWidth: "1200px" }}>
        {/* ========== Device Selection Section ========== */}
        <section
          id="device-selection"
          ref={deviceSectionRef}
          className="device-selection"
        >
          <h2 className="section-title">Select Your Device</h2>
          <div className="device-row">
            <Link to="/phone" className="device-item">
              <div className="icon-circle">
                <FaMobileAlt size={40} />
              </div>
              <p>PHONE</p>
            </Link>
            <Link to="/tablet" className="device-item">
              <div className="icon-circle">
                <FaTabletAlt size={40} />
              </div>
              <p>TABLET</p>
            </Link>

            <Link to="/laptop" className="device-item">
              <div className="icon-circle">
                <FaLaptop size={40} />
              </div>
              <p>LAPTOP</p>
            </Link>
            <Link to="/watch" className="device-item">
              <div className="icon-circle">
                <FaClock size={40} />
              </div>
              <p>WATCH</p>
            </Link>
            <Link to="/console" className="device-item">
              <div className="icon-circle">
                <FaGamepad size={40} />
              </div>
              <p>GAMING CONSOLES</p>
            </Link>
            <Link to="/services?device=accessories" className="device-item">
              <div className="icon-circle">
                <FaHeadphones size={40} />
              </div>
              <p>Accessories</p>
            </Link>
          </div>
        </section>

        {/* ========== How It Works Section ========== */}
        <section className="how-it-works">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-row">
            <div className="step-item">
              <span className="step-number">1</span>
              <h3>Pick Your Device & Issue</h3>
              <p>Tell us what type of device you have and what's wrong with it.</p>
            </div>
            <div className="step-item">
              <span className="step-number">2</span>
              <h3>Contact Us</h3>
              <p>For any questions regarding your repair, our experienced team is here to assist you. You can contact us on call, WhatsApp or email.</p>
            </div>
            <div className="step-item">
              <span className="step-number">3</span>
              <h3>Mail-In Service</h3>
              <p>Once you book a repair, you'll receive a shipping label to drop off your device at your nearest post office. We'll take care of the rest.</p>
            </div>
            <div className="step-item">
              <span className="step-number">4</span>
              <h3>Enjoy Your Repaired Device</h3>
              <p>After your repair is complete, we'll deliver your device straight to your doorstep, ready to use.</p>
            </div>
          </div>
        </section>
      </div>

      {/* ========== Customer Reviews Section ========== */}
      <CustomerReviews />
    </>
  );
}

export default Home;
